import type { Annotation, BBoxPixels, UserVisibility } from "@/types/annotations";
import type { ParsedKeypointAnnotation } from "./KeyPoints/types";
import { isBoxOutsideMeasurementBoundary, isPointInsideMeasurementBoundary } from "@shared/measurementBoundary";

export const OUTSIDE_BOUNDARY_CANDIDATE_COLOR = "#f59e0b";
export const OUTSIDE_BOUNDARY_CANDIDATE_OUTLINE_COLOR = "#b45309";

export type OutsideBoundaryCandidateState = "pending" | "rejected";

export type OutsideBoundaryKeypointRestoreState = {
  visibility: number;
  userVisibility: UserVisibility;
  hiddenReason?: ParsedKeypointAnnotation["hiddenReason"];
  visibleVisibility?: number;
};

export const isPointInsideMeasureBoundary = isPointInsideMeasurementBoundary;
export const isBBoxOutsideMeasureBoundary = (
  bbox: BBoxPixels,
  measureBoundaryPixels: Parameters<typeof isBoxOutsideMeasurementBoundary>[1]
): boolean => isBoxOutsideMeasurementBoundary(bbox, measureBoundaryPixels);

export const getOutsideBoundaryCandidateState = (
  annotation: Pick<Annotation, "userVisibility">
): OutsideBoundaryCandidateState | null => {
  if (annotation.userVisibility?.isVisible !== false) return null;
  const reasons = new Set(annotation.userVisibility.hiddenReasons ?? []);
  if (reasons.size === 1 && reasons.has("outsideBoundary")) return "pending";
  if (reasons.size === 2 && reasons.has("outsideBoundary") && reasons.has("userToggledOff")) return "rejected";
  return null;
};

export const isAnnotationEligibleForOutsideBoundaryReveal = (annotation: Pick<Annotation, "userVisibility">): boolean =>
  getOutsideBoundaryCandidateState(annotation) === "pending";

export const isAcceptedOutsideBoundaryAnnotation = (annotation: Pick<Annotation, "userVisibility">): boolean => {
  const visibility = annotation.userVisibility;
  const reasons = visibility?.hiddenReasons ?? [];
  return (
    visibility?.manualOverride === true &&
    reasons.includes("outsideBoundary") &&
    !reasons.includes("userToggledOff") &&
    (visibility.isVisible === true || reasons.some((reason) => reason !== "outsideBoundary"))
  );
};

// The boundary reason remains as provenance while manualOverride makes the
// annotation an ordinary visible/countable training annotation. Keeping that
// provenance makes the toggle reversible within the current unsaved editor
// mount without a separate staged-count path. Once saved, acceptance is data.
const promotedVisibility = (): UserVisibility => ({
  isVisible: true,
  manualOverride: true,
  hiddenReasons: ["outsideBoundary"],
});

const cloneUserVisibility = (visibility: UserVisibility): UserVisibility => ({
  ...visibility,
  ...(visibility.hiddenReasons ? { hiddenReasons: [...visibility.hiddenReasons] } : {}),
});

export const promoteOutsideBoundaryAnnotations = (
  annotations: Annotation[],
  annotationIds: ReadonlySet<string>
): Annotation[] =>
  annotations.map((annotation) =>
    annotationIds.has(annotation.id) && isAnnotationEligibleForOutsideBoundaryReveal(annotation)
      ? { ...annotation, userVisibility: promotedVisibility() }
      : annotation
  );

export const promoteOutsideBoundaryKeypoints = (
  annotations: ParsedKeypointAnnotation[],
  annotationIds: ReadonlySet<string>
): ParsedKeypointAnnotation[] =>
  annotations.map((annotation) =>
    annotationIds.has(annotation.id) && isAnnotationEligibleForOutsideBoundaryReveal(annotation)
      ? {
          ...annotation,
          keypoint: {
            ...annotation.keypoint,
            visibility: annotation.keypoint.visibility === 1 ? 1 : 2,
          },
          userVisibility: promotedVisibility(),
          hiddenReason: undefined,
          visibleVisibility: undefined,
        }
      : annotation
  );

export const demoteOutsideBoundaryAnnotations = (
  annotations: Annotation[],
  visibilityBeforePromotionById: ReadonlyMap<string, UserVisibility>
): Annotation[] => {
  if (
    visibilityBeforePromotionById.size === 0 ||
    !annotations.some(
      (annotation) =>
        visibilityBeforePromotionById.has(annotation.id) && isAcceptedOutsideBoundaryAnnotation(annotation)
    )
  ) {
    return annotations;
  }
  return annotations.map((annotation) => {
    const previousVisibility = visibilityBeforePromotionById.get(annotation.id);
    return previousVisibility && isAcceptedOutsideBoundaryAnnotation(annotation)
      ? { ...annotation, userVisibility: cloneUserVisibility(previousVisibility) }
      : annotation;
  });
};

export const demoteOutsideBoundaryKeypoints = (
  annotations: ParsedKeypointAnnotation[],
  stateBeforePromotionById: ReadonlyMap<string, OutsideBoundaryKeypointRestoreState>
): ParsedKeypointAnnotation[] => {
  if (
    stateBeforePromotionById.size === 0 ||
    !annotations.some(
      (annotation) => stateBeforePromotionById.has(annotation.id) && isAcceptedOutsideBoundaryAnnotation(annotation)
    )
  ) {
    return annotations;
  }
  return annotations.map((annotation) => {
    const previousState = stateBeforePromotionById.get(annotation.id);
    if (!previousState || !isAcceptedOutsideBoundaryAnnotation(annotation)) return annotation;

    const restored = {
      ...annotation,
      keypoint: { ...annotation.keypoint, visibility: previousState.visibility },
      userVisibility: cloneUserVisibility(previousState.userVisibility),
      hiddenReason: previousState.hiddenReason,
      visibleVisibility: previousState.visibleVisibility,
    };
    if (previousState.hiddenReason === undefined) delete restored.hiddenReason;
    if (previousState.visibleVisibility === undefined) delete restored.visibleVisibility;
    return restored;
  });
};
